import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import {
  ArrowLeft,
  Building2,
  FileText,
  Loader2,
  Mail,
  MapPin,
  Pencil,
  Phone,
  Plus,
  Search,
  Trash2,
  Upload,
  UserPlus,
} from 'lucide-react'
import { toast } from 'sonner'

import { AppShell } from '@/admin/components/app-shell'
import { BulkImportDialog } from '@/admin/components/imports/bulk-import-dialog'
import { AdminOnlyDialog } from '@/admin/components/shared/admin-only-dialog'
import { CardActionMenu } from '@/admin/components/shared/card-action-menu'
import { AttachmentsCard } from '@/admin/components/shared/attachments-card'
import { EditFormDialog } from '@/admin/components/shared/edit-form-dialog'
import { HostSearchInput } from '@/admin/components/shared/host-search-input'
import { DataTable, type DataTableColumn } from '@/admin/components/data-table'
import { usePermissions } from '@/admin/hooks/use-permissions'
import {
  useAddGroupContact,
  useAddGroupProperty,
  useCreateGroupClaim,
  useGroupClaims,
  useGroupRegistrations,
  useGroupTransfers,
  type GroupRegistrationRow,
  type GroupTransferRow,
  useGroupProperties,
  useRefreshGroup,
  usePropertyGroup,
  useRemoveGroupContact,
  useRemoveGroupProperty,
  useUpdateGroupContact,
  useUpdatePropertyGroup,
  type GroupClaimRow,
  type GroupContact,
  type GroupPropertyRow,
} from '@/admin/hooks/use-property-groups'
import {
  fromGroupAddress,
  toContactInput,
  toGroupAddress,
  useContactFields,
} from '@/admin/lib/property-group-forms'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs'
import { useFormatters } from '@/shared/lib/formatting'
import { EntityHistoryCard } from '@/shared/components/workflows/entity-history-card'
import { toastApiError } from '@/shared/lib/api-error'
import { defineField, formatPhoneDisplay } from '@/shared/lib/forms'
import type { FormFieldMeta } from '@/shared/lib/forms'

/** Group writes (add/remove property, contacts, details) are admin actions. */
// The permission the group's write endpoints check.
const EDIT_PERMISSION = 'groups.edit.global'

export function GroupDetailPage() {
  const { uuid = '' } = useParams<{ uuid: string }>()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { can } = usePermissions()
  const canEdit = can(EDIT_PERMISSION)

  const { data: group, isLoading } = usePropertyGroup(uuid)
  const { data: properties = [], isLoading: loadingProperties } =
    useGroupProperties(uuid)

  const updateGroup = useUpdatePropertyGroup(uuid)
  const addContact = useAddGroupContact(uuid)
  const updateContact = useUpdateGroupContact(uuid)
  const removeContact = useRemoveGroupContact(uuid)
  const addProperty = useAddGroupProperty(uuid)
  const removeProperty = useRemoveGroupProperty(uuid)

  const [editOpen, setEditOpen] = useState(false)
  const [contactDialog, setContactDialog] = useState<GroupContact | 'new' | null>(
    null,
  )
  const [addExistingOpen, setAddExistingOpen] = useState(false)
  const [removing, setRemoving] = useState<GroupPropertyRow | null>(null)
  // Adding and removing properties is for admins. Everyone sees the actions;
  // a non-admin who selects one is told who can do it, instead of a button
  // silently missing.
  const [askAdmin, setAskAdmin] = useState<string | null>(null)
  const [newClaimOpen, setNewClaimOpen] = useState(false)
  const createClaim = useCreateGroupClaim(uuid)
  const asAdmin = (action: string, run: () => void) =>
    canEdit ? run() : setAskAdmin(action)
  const [bulkOpen, setBulkOpen] = useState(false)
  const [claimUploadOpen, setClaimUploadOpen] = useState(false)
  const { data: claims = [], refetch: refetchClaims } = useGroupClaims(uuid)
  const refreshGroup = useRefreshGroup(uuid)
  const { data: registrations = [] } = useGroupRegistrations(uuid)
  const { data: transfers = [] } = useGroupTransfers(uuid)
  const { formatDate } = useFormatters()

  // A claim opened by hand: the group's registered properties to include, and
  // what happened. One line per chosen registration, as the upload makes.
  const newClaimFields = useMemo<FormFieldMeta[]>(
    () => [
      defineField({
        code_name: 'registrations',
        display_name: t('groups.detail.newClaimProperties', 'Affected properties'),
        hint: t('groups.detail.newClaimPropertiesHint', 'Search and tick each property on the claim'),
        field_type: 'MULTI_SELECT',
        required: true,
        options: registrations
          .filter((r) => r.can_claim)
          .map((r) => ({
            value: r.uuid,
            label: [r.property, r.product].filter(Boolean).join(' - '),
          })),
      }),
      defineField({
        code_name: 'description',
        display_name: t('groups.detail.newClaimDescription', 'What happened'),
        hint: t(
          'groups.detail.newClaimDescriptionHint',
          'Describe the damage and when it was first noticed',
        ),
        field_type: 'TEXTAREA',
        required: true,
      }),
    ],
    [t, registrations],
  )
  const canFileClaims = can('claims.create')

  const contactFields = useContactFields()

  const detailFields = useMemo<FormFieldMeta[]>(
    () => [
      defineField({
        code_name: 'name',
        display_name: t('groups.detail.name', '{{groupNoun}} Name'),
        field_type: 'TEXT',
        required: true,
      }),
      defineField({
        code_name: 'mailing_address',
        display_name: t('groups.detail.mailingAddress', '{{groupNoun}} Mailing Address'),
        field_type: 'ADDRESS',
        required: true,
        configuration_data: { lookup_provider: 'address' },
      }),
    ],
    [t],
  )


  if (isLoading) {
    return (
      <AppShell>
        <main className="page-container py-10">
          <p className="py-20 text-center text-[13px] text-[var(--ink-3)]">
            {t('common.loading', 'Loading...')}
          </p>
        </main>
      </AppShell>
    )
  }
  if (!group) {
    return (
      <AppShell>
        <main className="page-container py-10">
          <p className="py-20 text-center text-[13px] text-[var(--ink-3)]">
            {t('groups.detail.notFound', '{{groupNoun}} not found.')}
          </p>
        </main>
      </AppShell>
    )
  }

  const propertyColumns: DataTableColumn<GroupPropertyRow>[] = [
    {
      id: 'address',
      header: t('groups.detail.colProperty', 'Property'),
      cell: (row) => (
        <span className="text-[12px] text-[var(--ink)]">{row.address || '-'}</span>
      ),
    },
    {
      id: 'owner',
      header: t('groups.detail.colOwner', 'Owner'),
      cell: (row) => row.owner_name || '-',
    },
    {
      id: 'registered',
      header: t('groups.detail.colRegistered', 'Registered Products'),
      cell: (row) => row.registered_products,
    },
    {
      id: 'claims',
      header: t('groups.detail.colOpenClaims', 'Open Claims'),
      cell: (row) => row.open_claims,
    },
    {
      id: 'transfers',
      header: t('groups.detail.colOpenTransfers', 'Open Transfers'),
      cell: (row) => row.open_transfers,
    },
    // Only with rows: an empty pinned column draws a stray box in the header.
    ...(properties.length > 0
      ? ([
          {
            id: 'remove',
            header: '',
            // Pinned right so the row's actions stay in view at any width.
            sticky: 'right' as const,
            cell: (row: GroupPropertyRow) => (
              // Rarely used, so one quiet "more" button per row, not a
              // label on every line.
              <span onClick={(e) => e.stopPropagation()}>
                <CardActionMenu
                  triggerLabel={t('groups.detail.rowActions', 'More actions')}
                  triggerClassName="h-7 w-7 border-0 bg-transparent text-[var(--ink-3)] hover:text-[var(--ink)]"
                  items={[
                    {
                      key: 'remove',
                      label: t('groups.detail.removeProperty', 'Remove from {{groupNoun}}'),
                      icon: <Trash2 className="h-3.5 w-3.5" />,
                      destructive: true,
                      onSelect: () =>
                        asAdmin(t('groups.detail.adminRemove', 'remove a property from a {{groupNoun}}'), () =>
                          setRemoving(row),
                        ),
                    },
                  ]}
                />
              </span>
            ),
          },
        ] satisfies DataTableColumn<GroupPropertyRow>[])
      : []),
  ]

  const registrationColumns: DataTableColumn<GroupRegistrationRow>[] = [
    {
      id: 'number',
      header: t('property.colRegistration', 'Registration'),
      cell: (r) => <span className="text-[14px] text-[var(--ink)]">{r.registration_number}</span>,
    },
    {
      id: 'property',
      header: t('groups.detail.colProperty', 'Property'),
      cell: (r) => <span className="text-[13px] text-[var(--ink-2)]">{r.property}</span>,
    },
    {
      id: 'product',
      header: t('property.colProduct', 'Product'),
      cell: (r) => r.product || '-',
    },
    {
      id: 'class',
      header: t('property.colWarrantyClass', '{{warrantyNoun}} Class'),
      cell: (r) => r.warranty_class || '-',
    },
    {
      id: 'status',
      header: t('property.colStatus', 'Status'),
      cell: (r) => (
        <Badge variant={r.is_active ? 'secondary' : 'outline'}>{r.status_label}</Badge>
      ),
    },
    {
      id: 'registered',
      header: t('property.colRegistered', 'Registered'),
      cell: (r) => (r.registered_at ? formatDate(r.registered_at) : '-'),
    },
  ]

  const transferColumns: DataTableColumn<GroupTransferRow>[] = [
    {
      id: 'reference',
      header: t('property.colTransfer', 'Transfer'),
      cell: (tr) => <span className="text-[14px] text-[var(--ink)]">{tr.reference}</span>,
    },
    {
      id: 'property',
      header: t('groups.detail.colProperty', 'Property'),
      cell: (tr) => <span className="text-[13px] text-[var(--ink-2)]">{tr.property}</span>,
    },
    {
      id: 'status',
      header: t('property.colStatus', 'Status'),
      cell: (tr) => (
        <Badge variant={tr.is_open ? 'secondary' : 'outline'}>{tr.status_label}</Badge>
      ),
    },
    {
      id: 'updated',
      header: t('property.colUpdated', 'Last Updated'),
      cell: (tr) => (tr.updated_at ? formatDate(tr.updated_at) : '-'),
    },
  ]

  const claimColumns: DataTableColumn<GroupClaimRow>[] = [
    {
      id: 'number',
      header: t('groups.detail.colClaim', 'Claim'),
      cell: (c) => <span className="text-[14px] text-[var(--ink)]">{c.claim_number}</span>,
    },
    {
      id: 'covers',
      header: t('groups.detail.colCovers', 'Covers'),
      cell: (c) =>
        c.kind === 'group'
          ? t('groups.detail.coversProperties', {
              count: c.properties,
              defaultValue_one: '{{count}} property',
              defaultValue_other: '{{count}} properties',
            })
          : c.address,
    },
    {
      id: 'status',
      header: t('groups.detail.colStatus', 'Status'),
      cell: (c) => (
        <span className="flex items-center gap-2">
          <Badge variant={c.is_open ? 'secondary' : 'outline'}>
            {c.is_open ? t('property.open', 'Open') : t('property.closed', 'Closed')}
          </Badge>
          <span className="text-[13px] text-[var(--ink-2)]">{c.status_label}</span>
        </span>
      ),
    },
  ]

  return (
    <AppShell>
      <main className="page-container py-10">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/groups')}
          className="mb-4 cursor-pointer gap-1 px-0 text-[11px] text-[var(--ink-3)]"
        >
          <ArrowLeft className="h-3 w-3" />
          {t('groups.detail.back', 'All {{groupNounPlural}}')}
        </Button>

        {/* Header: the group's identity and how to reach it. */}
        <div className="mb-8 flex items-start gap-4">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full border-[0.5px] border-[var(--green-border)] bg-[var(--green-bg)]">
            <Building2 className="h-6 w-6 text-[var(--green)]" />
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="clara-title mb-1">{group.name}</h1>
            <div className="flex flex-wrap items-center gap-4">
              <span className="flex items-center gap-1.5 text-[12px] text-[var(--ink-3)]">
                <MapPin className="h-3 w-3" />
                {group.mailing_address_display || '-'}
              </span>
              <Badge variant="outline">
                {t('groups.detail.propertyCount', {
                  count: group.property_count,
                  defaultValue_one: '{{count}} property',
                  defaultValue_other: '{{count}} properties',
                })}
              </Badge>
            </div>
          </div>
          {canEdit && (
            <Button
              variant="outline"
              onClick={() => setEditOpen(true)}
              className="h-9 cursor-pointer gap-1.5"
            >
              <Pencil className="h-3.5 w-3.5" />
              {t('common.edit', 'Edit')}
            </Button>
          )}
        </div>

        <Tabs defaultValue="properties">
          <TabsList variant="line">
            <TabsTrigger value="properties">
              {t('groups.detail.tabProperties', 'Properties')}
            </TabsTrigger>
            <TabsTrigger value="registrations">
              {t('groups.detail.tabRegistrations', 'Registrations ({{count}})', {
                count: registrations.length,
              })}
            </TabsTrigger>
            <TabsTrigger value="claims">
              {t('groups.detail.tabClaims', 'Claims ({{count}})', { count: claims.length })}
            </TabsTrigger>
            <TabsTrigger value="transfers">
              {t('groups.detail.tabTransfers', 'Transfers ({{count}} open)', {
                count: transfers.filter((tr) => tr.is_open).length,
              })}
            </TabsTrigger>
            <TabsTrigger value="contacts">
              {t('groups.detail.tabContacts', 'Contacts')}
            </TabsTrigger>
            <TabsTrigger value="documents">
              {t('groups.detail.tabDocuments', 'Documents')}
            </TabsTrigger>
            <TabsTrigger value="changes">
              {t('groups.detail.tabChangeLog', 'Change Log')}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="properties" className="mt-6">
            <div className="mb-3 flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() =>
                  asAdmin(t('groups.detail.adminUpload', 'upload properties to a {{groupNoun}}'), () =>
                    setBulkOpen(true),
                  )
                }
                className="h-9 cursor-pointer gap-1.5"
              >
                <Upload className="h-3.5 w-3.5" />
                {t('groups.detail.bulkUpload', 'Upload Properties')}
              </Button>
              <Button
                variant="outline"
                onClick={() =>
                  asAdmin(t('groups.detail.adminAdd', 'add properties to a {{groupNoun}}'), () =>
                    setAddExistingOpen(true),
                  )
                }
                className="h-9 cursor-pointer gap-1.5"
              >
                <Search className="h-3.5 w-3.5" />
                {t('groups.detail.addExisting', 'Add Existing Property')}
              </Button>
              <Button
                onClick={() =>
                  asAdmin(t('groups.detail.adminAdd', 'add properties to a {{groupNoun}}'), () =>
                    navigate(`/properties/new?group=${uuid}`),
                  )
                }
                className="h-9 cursor-pointer gap-1.5"
              >
                <Plus className="h-3.5 w-3.5" />
                {t('groups.detail.addNew', 'Add New Property')}
              </Button>
            </div>
            <DataTable<GroupPropertyRow>
              tableId="group-properties"
              columns={propertyColumns}
              data={properties}
              rowKey={(row) => row.uuid}
              onRowClick={(row) => navigate(`/properties/${row.uuid}`)}
              emptyMessage={
                loadingProperties
                  ? t('common.loading', 'Loading...')
                  : t(
                      'groups.detail.noProperties',
                      'No properties yet. Add an existing property or a new one.',
                    )
              }
            />
          </TabsContent>

          <TabsContent value="claims" className="mt-6">
            {canFileClaims && group.claim_upload_target && (
              <div className="mb-3 flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => setClaimUploadOpen(true)}
                  className="h-9 cursor-pointer gap-1.5"
                >
                  <Upload className="h-3.5 w-3.5" />
                  {t('groups.detail.uploadClaims', 'Upload Claims')}
                </Button>
                <Button
                  onClick={() => setNewClaimOpen(true)}
                  className="h-9 cursor-pointer gap-1.5"
                >
                  <Plus className="h-3.5 w-3.5" />
                  {t('groups.detail.newClaim', 'New Claim')}
                </Button>
              </div>
            )}
            <DataTable<GroupClaimRow>
              tableId="group-claims"
              columns={claimColumns}
              data={claims}
              rowKey={(c) => c.uuid}
              onRowClick={(c) => navigate(`/claims/${c.uuid}`)}
              emptyMessage={t(
                'groups.detail.noClaims',
                'No claims yet. Upload the {{groupNoun}} claim file to open one claim for all affected properties.',
              )}
            />
          </TabsContent>

          <TabsContent value="registrations" className="mt-6">
            <DataTable<GroupRegistrationRow>
              tableId="group-registrations"
              columns={registrationColumns}
              data={registrations}
              rowKey={(r) => r.uuid}
              onRowClick={(r) => navigate(`/registrations/${r.uuid}`)}
              emptyMessage={t(
                'groups.detail.noRegistrations',
                'No registrations yet. Register a property from its page, then copy the registration to the other properties.',
              )}
            />
          </TabsContent>

          <TabsContent value="transfers" className="mt-6">
            <DataTable<GroupTransferRow>
              tableId="group-transfers"
              columns={transferColumns}
              data={transfers}
              rowKey={(tr) => tr.uuid}
              onRowClick={(tr) => navigate(`/transfers/${tr.uuid}`)}
              emptyMessage={t('groups.detail.noTransfers', 'No transfers on these properties.')}
            />
          </TabsContent>

          <TabsContent value="contacts" className="mt-6">
            {canEdit && (
              <div className="mb-3 flex justify-end">
                <Button
                  variant="outline"
                  onClick={() => setContactDialog('new')}
                  className="h-9 cursor-pointer gap-1.5"
                >
                  <UserPlus className="h-3.5 w-3.5" />
                  {t('groups.detail.addContact', 'Add Contact')}
                </Button>
              </div>
            )}
            <div className="flex flex-col gap-2">
              {group.contacts.map((c) => (
                <div
                  key={c.uuid}
                  className="flex items-center gap-4 rounded-lg border-[0.5px] border-[var(--rule)] bg-[var(--bg-light)] px-4 py-3"
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-[13px] text-[var(--ink)]">
                      {`${c.first_name} ${c.last_name}`.trim()}
                      {c.is_primary && (
                        <Badge variant="secondary" className="ml-2">
                          {t('groups.detail.primary', 'Primary')}
                        </Badge>
                      )}
                    </p>
                    <div className="mt-1 flex flex-wrap gap-4 text-[12px] text-[var(--ink-3)]">
                      {c.email && (
                        <span className="flex items-center gap-1.5">
                          <Mail className="h-3 w-3" />
                          {c.email}
                        </span>
                      )}
                      {c.phone && (
                        <span className="flex items-center gap-1.5">
                          <Phone className="h-3 w-3" />
                          {formatPhoneDisplay(c.phone)}
                        </span>
                      )}
                    </div>
                  </div>
                  {canEdit && (
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        aria-label={t('common.edit', 'Edit')}
                        onClick={() => setContactDialog(c)}
                        className="cursor-pointer"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        aria-label={t('groups.detail.removeContact', 'Remove contact')}
                        onClick={async () => {
                          try {
                            await removeContact.mutateAsync(c.uuid)
                            toast.success(t('groups.detail.contactRemoved', 'Contact removed'))
                          } catch (err) {
                            toastApiError(err, t('groups.detail.contactRemoveFailed', 'Could not remove the contact'))
                          }
                        }}
                        className="cursor-pointer text-[var(--ink-3)] hover:text-[var(--status-error)]"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="documents" className="mt-6">
            <AttachmentsCard
              subjectType="group"
              subjectUuid={group.uuid}
              // Bylaws and proof of ownership: who is responsible for the roofs.
              useCase="PROOF_OF_OWNERSHIP"
              title={t('groups.detail.documents', 'Proof of Ownership and Bylaws')}
              icon={<FileText className="h-3.5 w-3.5" />}
              canAdd={canEdit}
              canDelete={canEdit}
              emptyText={t(
                'groups.detail.noDocuments',
                'No documents yet. Attach the {{groupNoun}} bylaws or proof of purchase.',
              )}
            />
          </TabsContent>

          <TabsContent value="changes" className="mt-6">
            <EntityHistoryCard
              entityKey="group"
              entityId={group.uuid}
              title={t('groups.detail.changeLog', 'Change Log')}
            />
          </TabsContent>
        </Tabs>

        <EditFormDialog
          open={editOpen}
          onOpenChange={setEditOpen}
          title={t('groups.detail.editTitle', 'Edit {{groupNoun}}')}
          fields={detailFields}
          values={{
            name: group.name,
            mailing_address: fromGroupAddress(group.mailing_address),
          }}
          isPending={updateGroup.isPending}
          onSave={async (values) => {
            try {
              await updateGroup.mutateAsync({
                name: String(values.name ?? ''),
                mailing_address: toGroupAddress(values.mailing_address),
              })
              toast.success(t('groups.detail.saved', '{{groupNoun}} updated'))
            } catch (err) {
              toastApiError(err, t('groups.detail.saveFailed', 'Could not update the {{groupNoun}}'))
              return false
            }
          }}
        />

        <EditFormDialog
          open={contactDialog !== null}
          onOpenChange={(o) => !o && setContactDialog(null)}
          title={
            contactDialog === 'new'
              ? t('groups.detail.addContact', 'Add Contact')
              : t('groups.detail.editContact', 'Edit Contact')
          }
          fields={contactFields}
          values={
            contactDialog && contactDialog !== 'new'
              ? { ...contactDialog }
              : { first_name: '', last_name: '', email: '', phone: '' }
          }
          isPending={addContact.isPending || updateContact.isPending}
          onSave={async (values) => {
            const data = toContactInput(values)
            try {
              if (contactDialog === 'new') {
                await addContact.mutateAsync(data)
              } else if (contactDialog) {
                await updateContact.mutateAsync({ contactUuid: contactDialog.uuid, data })
              }
              toast.success(t('groups.detail.contactSaved', 'Contact saved'))
              setContactDialog(null)
            } catch (err) {
              toastApiError(err, t('groups.detail.contactSaveFailed', 'Could not save the contact'))
              return false
            }
          }}
        />

        {claimUploadOpen && group.claim_upload_target && (
          <BulkImportDialog
            open={claimUploadOpen}
            onOpenChange={setClaimUploadOpen}
            target={group.claim_upload_target}
            extraParams={{ group: group.uuid }}
            title={t('groups.detail.uploadClaimsTitle', 'Upload Claims for {{name}}', {
              name: group.name,
            })}
            onComplete={() => void refetchClaims()}
          />
        )}

        {bulkOpen && (
          <BulkImportDialog
            open={bulkOpen}
            onOpenChange={setBulkOpen}
            target="group_members"
            extraParams={{ group: group.uuid }}
            title={t('groups.bulk.title', 'Upload Properties to {{name}}', {
              name: group.name,
            })}
            onComplete={refreshGroup}
          />
        )}

        <Dialog open={addExistingOpen} onOpenChange={setAddExistingOpen}>
          <DialogContent className="sm:max-w-[560px]">
            <DialogHeader>
              <DialogTitle className="text-[15px] font-normal text-[var(--ink)]">
                {t('groups.detail.addExistingTitle', 'Add an Existing Property')}
              </DialogTitle>
              <DialogDescription className="text-[12px] text-[var(--ink-3)]">
                {t(
                  'groups.detail.addExistingDescription',
                  'Find a property already in the system and add it to {{name}}.',
                  { name: group.name },
                )}
              </DialogDescription>
            </DialogHeader>
            <HostSearchInput
              onSelect={async (result) => {
                try {
                  await addProperty.mutateAsync(result.uuid)
                  toast.success(t('groups.detail.propertyAdded', 'Property added'))
                  setAddExistingOpen(false)
                } catch (err) {
                  toastApiError(err, t('groups.detail.propertyAddFailed', 'Could not add the property'))
                }
              }}
            />
          </DialogContent>
        </Dialog>

        <AdminOnlyDialog action={askAdmin} onClose={() => setAskAdmin(null)} />

        <EditFormDialog
          open={newClaimOpen}
          onOpenChange={setNewClaimOpen}
          title={t('groups.detail.newClaimTitle', 'New claim for {{name}}', { name: group.name })}
          fields={newClaimFields}
          values={{ registrations: [], description: '' }}
          isPending={createClaim.isPending}
          submitLabel={t('groups.detail.newClaimSubmit', 'Create Claim')}
          onSave={async (values) => {
            try {
              const claim = await createClaim.mutateAsync({
                registrations: (values.registrations as string[]) ?? [],
                description: String(values.description ?? ''),
              })
              toast.success(
                t('groups.detail.newClaimCreated', 'Claim {{number}} opened', {
                  number: claim.claim_number,
                }),
              )
              setNewClaimOpen(false)
              navigate(`/claims/${claim.uuid}`)
            } catch (err) {
              toastApiError(err, t('groups.detail.newClaimFailed', 'Could not open the claim'))
              return false
            }
          }}
        />

        <Dialog open={removing !== null} onOpenChange={(o) => !o && setRemoving(null)}>
          <DialogContent className="sm:max-w-[460px]">
            <DialogHeader>
              <DialogTitle className="text-[15px] font-normal text-[var(--ink)]">
                {t('groups.detail.removeTitle', 'Remove this property from the {{groupNoun}}?')}
              </DialogTitle>
              <DialogDescription className="text-[12px] text-[var(--ink-3)]">
                {t(
                  'groups.detail.removeDescription',
                  '{{address}} stays in the system with its registrations and claims. It is only taken out of {{name}}, and the change is logged.',
                  { address: removing?.address ?? '', name: group.name },
                )}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setRemoving(null)}
                className="cursor-pointer"
              >
                {t('common.cancel', 'Cancel')}
              </Button>
              <Button
                variant="destructive"
                disabled={removeProperty.isPending}
                onClick={async () => {
                  if (!removing) return
                  try {
                    await removeProperty.mutateAsync(removing.uuid)
                    toast.success(t('groups.detail.propertyRemoved', 'Property removed from the {{groupNoun}}'))
                    setRemoving(null)
                  } catch (err) {
                    toastApiError(err, t('groups.detail.propertyRemoveFailed', 'Could not remove the property'))
                  }
                }}
                className="cursor-pointer gap-1.5"
              >
                {removeProperty.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                {t('groups.detail.removeConfirm', 'Remove')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </AppShell>
  )
}
